import { useRef } from "react";
import Image from "next/image";
import {
  FaEye,
  FaHandsHelping,
} from "react-icons/fa";
import MainLayout from "@/Component/MainLayout";
import Breadcrumbs from "@/Component/Common/Breadcrumbs";
import PageHero from "@/Component/Common/PageHero";
import LogoMarquee, {
  AWARD_IMAGES,
  FEATURED_IMAGES,
  PARTNER_IMAGES,
} from "./LogoMarquee";
import styles from "./AboutUs.module.css";
import DownloadNH from "@/Component/Common/DownloadNH";
import { isIndianUser } from "@/utils/getCountryCode";
import { RiShieldCheckFill, RiTargetFill } from "react-icons/ri";

const WHO_WE_ARE = isIndianUser() ?
  [
    "Next Holidays India started this journey as an Online Travel Company in 2024, from India to the world. Being a part of Blueberry Group, we deliver an array of exemplary Domestic and International tour packages. With our dedicated team of tourism professionals and experience in B2C travel services, Next Holidays provides affordable and quality travel services on the customer's end.",
    "With a mission to make travel easy and memorable, Next Holidays has built a reputation for curating personalized itineraries that serve the individual needs and likes of every traveler. From serene hill stations and sun-kissed beaches to lively international adventures, we assure every journey is packed with amazing experiences."
  ]
  :
  [
    "Next Holidays began its journey in 2024 as an online travel brand, connecting travellers from India with destinations across the world. As a part of the Blueberry Group, we offer a diverse range of thoughtfully curated domestic and international tour packages designed to make travel simple, seamless, and memorable.",
    "Backed by a dedicated team of tourism professionals and experience in B2C travel services, Next Holidays is committed to delivering quality travel experiences at affordable prices.",
    "With a mission to make every journey easy and memorable, we curate personalized itineraries based on the individual preferences, interests, and needs of every traveller. From serene hill stations and sun-kissed beaches to vibrant international destinations, we strive to create journeys filled with meaningful experiences, memorable moments, and lasting memories."
  ];

const VALUE_CARDS = [
  {
    id: "mission",
    title: "Mission",
    text: "We help every traveler book with ease, find the best deals, and get service made just for them.",
    icon: RiTargetFill,
  },
  {
    id: "vision",
    title: "Vision",
    text: "To be the most trusted name in travel, helping people create memories that last a lifetime.",
    icon: FaEye,
  },
  {
    id: "values",
    title: "Core Values",
    text: "Our values are simple: make booking easy, offer real deals, and treat every traveler like family.",
    icon: RiShieldCheckFill,
  },
];

const FOOTPRINT_STATS = [
  { value: "60+", label: "Activities" },
  { value: "5K+", label: "Satisfied Travelers" },
  { value: "120+", label: "Destinations" },
  { value: "100+", label: "Partners" },
];

const WHY_CHOOSE = [
  {
    title: "Unbeatable Deals",
    text: "Our deals help you travel more while spending less.",
    icon: <Image src={"/assets/icons/fireDisc.svg"} alt="" height={24} width={24} />,
  },
  {
    title: "Secure transactions",
    text: "Book with confidence. Your transactions are always protected.",
    icon: <RiShieldCheckFill size={24} />,
  },
  {
    title: "Trust & reliability",
    text: "Reliable service you can count on, every single time.",
    icon: <Image src={"/assets/icons/startrust.svg"} alt="" height={24} width={24} />,
  },
  {
    title: "Best travel companion",
    text: "One platform for all your travel needs, start to finish.",
    icon: <FaHandsHelping size={24} />,
  },
];

function SectionHeader({ title, subtitle }) {
  return (
    <div className={styles.sectionHeader}>
      <h2 className="standard-heading">{title}</h2>
      {subtitle ? <p className="standard-subheading">{subtitle}</p> : null}
    </div>
  );
}

function ValueCard({ title, text, icon: Icon }) {
  return (
    <article className={styles.valueCard}>
      <span className={styles.valueIconWrap} aria-hidden="true">
        <Icon size={24} />
      </span>
      <h3 className={styles.valueTitle}>{title}</h3>
      <p className={styles.valueText}>{text}</p>
    </article>
  );
}

function FeatureCard({ title, text, icon }) {
  return (
    <article className={styles.featureCard}>
      <span className={styles.featureIconWrap} aria-hidden="true">
        {icon}
      </span>
      <h3 className={styles.featureTitle}>{title}</h3>
      <p className={styles.featureText}>{text}</p>
    </article>
  );
}

export default function AboutUs() {
  const contentRef = useRef(null);

  return (
    <MainLayout hideHeader>
      <PageHero
        webImageUrl={isIndianUser() ? "/assets/images/AboutBannerIndia.webp" : "/assets/images/AboutBanner.webp"}
        alt="About us banner"
        scrollTargetRef={contentRef}
      />
      <Breadcrumbs />
      <section
        ref={contentRef}
        id="who-we-are"
        className={`standard-padding ${styles.whoSection}`}
      >
        <div className={styles.whoGrid} >
          <div className={styles.whoCopy}>
            <h2 className={`standard-heading ${styles.heading}`}>Our Story</h2>
            {WHO_WE_ARE.map((paragraph) => (
              <p key={paragraph.slice(0, 24)} className={styles.bodyText}>
                {paragraph}
              </p>
            ))}
          </div>

          <div className={styles.videoWrap}>
            <iframe
              src="https://www.youtube.com/embed/7DuW_LWm8Pw"
              className={styles.AboutVideo}
              title="About Next Holidays"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      <section className={`standard-padding ${styles.valuesSection}`}>
        <SectionHeader
          title="Mission, Vision & Values "
          subtitle="A growing network of travelers who trust us."
        />
        <div className={styles.valueGrid}>
          {VALUE_CARDS.map((card) => (
            <ValueCard key={card.id} {...card} />
          ))}
        </div>
      </section>

      <section className={`standard-padding ${styles.blockSection}`}>
        <SectionHeader
          title="Our Awards"
          subtitle="Our wide business network and loyal customer base."
        />
        <LogoMarquee items={AWARD_IMAGES} duration={20} />
      </section>

      <section className={`standard-padding ${styles.blockSection}`}>
        <SectionHeader
          title="Our Footprint"
          subtitle="Numbers that show how far we've come."
        />
        <div className={styles.statsGrid}>
          {FOOTPRINT_STATS.map((stat) => (
            <div key={stat.label} className={styles.statCell}>
              <div className={styles.statInner}>
                <p className={styles.statValue}>{stat.value}</p>
                <p className={styles.statLabel}>{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className={`standard-padding ${styles.blockSection}`}>
        <SectionHeader
          title="Our Partners"
          subtitle="The best brands, working together for you."
        />
        <LogoMarquee items={PARTNER_IMAGES} duration={20} />
      </section>

      <section className={`standard-padding ${styles.blockSection}`}>
        <SectionHeader
          title="Why Travelers Choose Us"
          subtitle="See what makes us stand out."
        />
        <div className={styles.featureGrid}>
          {WHY_CHOOSE.map((item) => (
            <FeatureCard key={item.title} {...item} />
          ))}
        </div>
      </section>

      <section className={`standard-padding ${styles.blockSection}`}>
        <SectionHeader
          title="Top picks for you"
          subtitle="Some of our favorite moments and offers."
        />
        <LogoMarquee items={FEATURED_IMAGES} duration={20} />
      </section>

      <DownloadNH />
    </MainLayout>
  );
}
